/**
 * The words for the reasons to come back (D304): the day's village orders, the themed week and
 * the daily visit gift. Plain and short, as docs/STYLE.md has it.
 */
import { idleOrders, type IdleEventView, type IdleWorldState, type IdleGift } from '@hedgerow/sim';
import type { IdleOrderView, IdleRosetteView, IdleWelcomeBack } from '@hedgerow/sim';
import type { IdleContent } from '@hedgerow/data';
import { coins, num } from './notation.ts';
import { bonusText, clockAt, fmtSpan, shouldWelcome, welcomeLines } from './view.ts';
import { cap, t as term } from './terms.ts';

/** An order as the village asks for it: `Send 500 eggs`. */
export function orderTitle(o: IdleOrderView): string {
  return `Send ${num(o.count)} ${o.goods}`;
}

/** How far an order has got: `320 of 500 made. Pays 1.2Kc`, or `Sent. Paid 1,200c`. */
export function orderLine(o: IdleOrderView): string {
  if (o.done) return `Sent. Paid ${coins(o.coins)}`;
  if (o.ready) return `Ready to send. Pays ${coins(o.coins)}`;
  return `${num(o.made)} of ${num(o.count)} made. Pays ${coins(o.coins)}`;
}

/** When the next orders come: `New orders at 01:00 tomorrow.` */
export function ordersNext(at: number, t: number): string {
  return `New orders at ${clockAt(at, t)}.`;
}

/** The orders card on the world's first day, before any have come (D364: the theme's own words). */
export function ordersFirstDay(): string {
  return term('ordersFirstDay');
}

/** The week's goal: `Fill 12 orders this week` or `Visit on 4 days this week`. */
export function eventGoal(e: IdleEventView): string {
  return e.event.goal.kind === 'orders'
    ? `Fill ${e.goal} orders this week`
    : `Visit on ${e.goal} days this week`;
}

/** How far the week's goal has got, and its prize. */
export function eventLine(e: IdleEventView): string {
  if (e.won) return `Done. ${e.event.prize.name} is in your collection.`;
  return `${e.progress} of ${e.goal}. Prize: ${e.event.prize.name}, this week only.`;
}

/** When the week ends: `Ends in 3d 4h.` */
export function eventEnds(e: IdleEventView, t: number): string {
  return `Ends in ${fmtSpan(e.endsAt - t)}.`;
}

/**
 * D326/D364: the rosettes earned and the income bonus they give: `3 rosettes earned. All income
 * +16%.`, or `No rosettes yet.` with none; the theme's own word in place of "rosette".
 */
export function rosetteLine(r: IdleRosetteView): string {
  const one = term('rosette');
  const many = `${one}s`;
  if (r.count === 0) return `No ${many} yet.`;
  return `${r.count} ${r.count === 1 ? one : many} earned. All income ${bonusText(r.bonus)}.`;
}

/**
 * D326/D364: progress towards the next rosette, like Egg, Inc.'s monthly Prophecy Egg: `2 of 3
 * weeks this month.`, or `All rosettes earned.` once the data's cap is reached.
 */
export function rosetteProgress(r: IdleRosetteView): string {
  if (r.capped) return `All ${term('rosette')}s earned.`;
  return `${r.weeks} of ${r.need} weeks this month.`;
}

/** D364: the Region tab's rosette card title: "Rosettes", "Badges". */
export function rosettesTitle(): string {
  return `${cap(term('rosette'))}s`;
}

/** The gift as the welcome back says it: `Daily gift: 90c. 2 days in a row.` */
export function giftLine(g: IdleGift): string {
  const run = g.streak > 1 ? `${g.streak} days in a row.` : '';
  return run ? `Daily gift: ${coins(g.coins)}. ${run}` : `Daily gift: ${coins(g.coins)}.`;
}

/**
 * The welcome back's head (D304, D260): the gift rides on the welcome back when there is one,
 * and has the card to itself when the player was not away long enough for one. One card, never
 * two stacked.
 */
export function welcomeHead(w: IdleWelcomeBack): {
  title: string;
  amount: string;
  line: string | null;
  gift: string | null;
} {
  const gift = w.gift ? giftLine(w.gift) : null;
  if (w.gift && !shouldWelcome(w))
    return { title: 'Daily gift', amount: `+${coins(w.gift.coins)}`, line: null, gift };
  return { ...welcomeLines(w), gift };
}

/** True when an open shows the welcome card: back from long enough away, or a gift to give. */
export function showWelcome(w: IdleWelcomeBack): boolean {
  return shouldWelcome(w) || !!w.gift;
}

/** Orders that have all they ask for and wait to be sent. */
export function ordersReady(world: IdleWorldState, content: IdleContent, t: number): number {
  return idleOrders(world, content, t).filter((o) => o.ready).length;
}

/** The prompt bar's words for orders ready (D285): `An order is ready`, `2 orders are ready`. */
export function orderPrompt(n: number): string | null {
  if (n <= 0) return null;
  return n === 1 ? 'An order is ready' : `${n} orders are ready`;
}
